import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { exemplairesColumnsVisibility } from "@/config/dashboard/exemplaires";
import { publicAuctionColumnsVisibility } from "@/config/dashboard/public-auction";
import { FormLayoutElement } from "@/config/forms/general";
import { SupportedLocale } from "@/i18n";
import { Auction, MultipleSampleInAuction, Multiplesample } from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import {
  AuctionLinkGridCard,
  MultiplesampleLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import MultiplesampleInAuctionDrawer from "./drawer/MultiplesampleInAuctionDrawer";
import { formatMultiplesampleAuthorDisplay } from "./multiplesampleAuthorDisplay";

interface MultiplesampleInAuctionLinksProps {
  id: string;
  multiplesample: Multiplesample | undefined;
  auction: Auction | undefined;
  type: LinkableType;
  fields: FormLayoutElement[] | undefined;
}

const columnsAuction = [
  {
    accessorKey: "ventesDescriptionNumeroVente",
    header: "ventesDescriptionNumeroVente",
    cell: ({ row }: { row: Row<MultipleSampleInAuction> }) => (
      <TextCell value={row.original.auction.ventesDescriptionNumeroVente} />
    ),
  },
  {
    accessorKey: "vignette",
    header: "",
    size: 100,
    cell: ({ row }: { row: Row<MultipleSampleInAuction> }) => (
      <ImageCell
        value=""
        attachments={row.original.auction?.attachments ?? []}
        domain="auction"
      />
    ),
  },
  {
    accessorKey: "ventesDescriptionCommissaires",
    header: "ventesDescriptionCommissaires",
    cell: ({ row }: { row: Row<MultipleSampleInAuction> }) => (
      <TextCell value={row.original.auction.ventesDescriptionCommissaires} />
    ),
  },
  {
    accessorKey: "ventesDescriptionDate",
    header: "ventesDescriptionDate",
    cell: ({ row }: { row: Row<MultipleSampleInAuction> }) => (
      <TextCell value={row.original.auction.ventesDescriptionDate} />
    ),
  },
  {
    accessorKey: "ventesDescriptionVille",
    header: "ventesDescriptionVille",
    cell: ({ row }: { row: Row<MultipleSampleInAuction> }) => (
      <TextCell value={row.original.auction.ventesDescriptionVille} />
    ),
  },
  {
    accessorKey: "ventesDescriptionPays",
    header: "ventesDescriptionPays",
    cell: ({ row }: { row: Row<MultipleSampleInAuction> }) => (
      <TextCell value={row.original.auction.ventesDescriptionPays} />
    ),
  },
];

const emptyMultiplesampleInAuction = (
  multiplesample: Multiplesample,
  auction: Auction,
): MultipleSampleInAuction => ({
  id: "",
  multiplesample,
  auction,
  multiplesampleDansVentesDescriptionNumeroLot: "",
  multiplesampleDansVentesDescriptionReproduitPage: "",
  multiplesampleDansVentesDescriptionEstimationHaute: "",
  multiplesampleDansVentesDescriptionEstimationBasse: "",
  multiplesampleDansVentesDescriptionVendu: false,
  multiplesampleDansVentesDescriptionResultatNonListe: false,
  multiplesampleDansVentesDescriptionNonVenu: false,
  multiplesampleDansVentesDescriptionNonVendu: false,
  multiplesampleDansVentesDescriptionNonReproduit: false,
  multiplesampleDansVentesDescriptionRetire: false,
  multiplesampleDansVentesDescriptionMiseAPrix: "",
  multiplesampleDansVentesDescriptionPrixMarteau: "",
  multiplesampleDansVentesDescriptionPrixAvecFrais: "",
  multiplesampleDansVentesDescriptionNotes: "",
});

const MultiplesampleInAuctionLinks = ({
  id,
  multiplesample,
  auction,
  fields,
  type,
}: MultiplesampleInAuctionLinksProps) => {
  const { t, i18n } = useTranslation();

  const { data: relatedData } = useGetComplexRelationFields(
    id,
    "multipleSampleInAuction",
    type,
  );

  const [searchContent, setSearchContent] = useState("");
  const { mutate: deleteLink } = useDeleteComplexLink(
    id,
    "multipleSampleInAuction",
    type,
  );
  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    async (linkId: string) => {
      await deleteLink({
        id: linkId,
        relation: "multipleSampleInAuction",
      });
    },
    [deleteLink],
  );

  const renderMultipleSampleInAuctionRowActions = useCallback(
    (data: MultipleSampleInAuction) => {
          const preview =
            type === "auction"
              ? {
                  previewType: "multiplesample" as const,
                  previewId: data.multiplesample.id,
                  previewElements: [
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ??
                        "",
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNumero ??
                        "",
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNotes ??
                        "",
                      type: "richtext" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire ??
                        "",
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionLocalisation ??
                        "",
                      type: "text" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.multiplesample.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "multiplesample"
                ? {
                    previewType: "auction" as const,
                    previewId: data.auction.id,
                    previewElements: [
                      {
                        label: t("ventesDescriptionNumeroVente"),
                        value: data.auction.ventesDescriptionNumeroVente,
                        type: "text" as const,
                      },
                      {
                        label: t("ventesDescriptionResume"),
                        value: data.auction.ventesDescriptionResume ?? "",
                        type: "richtext" as const,
                      },
                      {
                        label: t("attachments"),
                        value: data.auction.attachments ?? [],
                        type: "image" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInAuctionDrawer
                  key={data.id}
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<MultipleSampleInAuction, unknown>[] = useMemo(
    () => [
      ...(type === "multiplesample"
        ? (columnsAuction.map((col) => ({
            ...col,
            header: typeof col.header === "string" ? t(col.header) : col.header,
          })) as ColumnDef<MultipleSampleInAuction, unknown>[])
        : type === "auction"
          ? [
              {
                accessorKey:
                  "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
                header: t(
                  "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
                ),
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInAuction>;
                }) => (
                  <TextCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement
                    }
                  />
                ),
              },
              {
                accessorKey:
                  "exemplaireDansEstampesMultiplesDescriptionNumero",
                header: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInAuction>;
                }) => (
                  <TextCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionNumero
                    }
                  />
                ),
              },
              {
                accessorKey: "vignette",
                header: "",
                size: 100,
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInAuction>;
                }) => (
                  <ImageCell
                    value=""
                    attachments={
                      row.original.multiplesample?.attachments ?? []
                    }
                    domain="multiplesample"
                  />
                ),
              },
              {
                accessorKey:
                  "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
                header: t(
                  "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
                ),
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInAuction>;
                }) => (
                  <TextCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire
                    }
                  />
                ),
              },
              {
                accessorKey: "author",
                header: t("tab_relatedAuthor"),
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInAuction>;
                }) => (
                  <TextCell
                    value={formatMultiplesampleAuthorDisplay(
                      row.original.multiplesample,
                      i18n.language as SupportedLocale,
                    )}
                  />
                ),
              },
              {
                accessorKey: "exemplaireDansEstampesMultiplesDescriptionLocalisation",
                header: t(
                  "exemplaireDansEstampesMultiplesDescriptionLocalisation",
                ),
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInAuction>;
                }) => (
                  <TextCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionLocalisation
                    }
                  />
                ),
              },
              {
                accessorKey: "exemplaireDansEstampesMultiplesDescriptionNotes",
                header: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                cell: ({
                  row,
                }: {
                  row: Row<MultipleSampleInAuction>;
                }) => (
                  <HtmlCell
                    value={
                      row.original.multiplesample
                        ?.exemplaireDansEstampesMultiplesDescriptionNotes ?? ""
                    }
                  />
                ),
              },
            ]
          : []),
      {
        accessorKey: "multiplesampleDansVentesDescriptionNumeroLot",
        header: t("auctionLotNumber"),
      },
      ...(type === "auction"
        ? [
            {
              accessorKey: "multiplesampleDansVentesDescriptionNotes",
              header: t("multiplesampleDansVentesDescriptionNotes"),
              cell: ({
                row,
              }: {
                row: Row<MultipleSampleInAuction>;
              }) => (
                <HtmlCell
                  value={row.original.multiplesampleDansVentesDescriptionNotes ?? ""}
                />
              ),
            },
          ]
        : []),
      createActionsColumnDef<MultipleSampleInAuction>(({ row }) =>
        renderMultipleSampleInAuctionRowActions(row.original),
      ),
    ],
    [fields, handleDeleteRelation, id, i18n.language, t, type],
  );

  const datatableConf = useMemo(() => {
    if (type === "multiplesample") {
      return (
        <MultiplesampleToAuctionLinks
          id={id}
          relatedData={relatedData}
          fields={fields}
          multiplesample={multiplesample}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleSampleInAuctionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("auctionsRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
    return (
      <AuctionToMultiplesampleLinks
        id={id}
        type={type}
        relatedData={relatedData}
        fields={fields}
        auction={auction}
        isAddMode={isAddMode}
        columnsLink={columns}
        renderRowActions={renderMultipleSampleInAuctionRowActions}
        searchContent={searchContent}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        listGridTitle={t("multiplesamplesRelated")}
        layoutBackLabel={t("relatedRecords")}
        setIsAddMode={setIsAddMode}
      />
    );
  }, [
    type,
    id,
    relatedData,
    fields,
    multiplesample,
    isAddMode,
    columns,
    searchContent,
    auction,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

type LinksTableProps<T> = {
  id: string;
  relatedData: T[] | undefined;
  fields: FormLayoutElement[] | undefined;
  isAddMode: boolean;
  columnsLink: ColumnDef<T, unknown>[];
  renderRowActions?: (row: T) => ReactNode;
  searchContent: string;
  searchInput?: ReactNode;
  listGridTitle?: ReactNode;
  layoutBackLabel?: ReactNode;
  setIsAddMode?: Dispatch<SetStateAction<boolean>>;
};

type MultiplesampleToAuctionLinksProps =
  LinksTableProps<MultipleSampleInAuction> & {
    multiplesample: Multiplesample | undefined;
  };

type AuctionToMultiplesampleLinksProps =
  LinksTableProps<MultipleSampleInAuction> & {
    auction: Auction | undefined;
    type: LinkableType;
  };

const MultiplesampleToAuctionLinks = ({
  id,
  relatedData,
  fields,
  multiplesample,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: MultiplesampleToAuctionLinksProps) => {
  const { t } = useTranslation();
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "multiplesample-links-auction-add";
  const STORAGE_KEY_LINKED = "multiplesample-links-auction-linked";
  const defaultVisibility = {
    ...publicAuctionColumnsVisibility,
    actions: true,
  };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedAuctions } = useGetNotRelatedResource<Auction>(
    {
      resultType: "auction",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    },
  );

  const columnsAuctions: ColumnDef<Auction, unknown>[] = useMemo(
    () => [
      {
        accessorKey: "ventesDescriptionNumeroVente",
        header: t("ventesDescriptionNumeroVente"),
        cell: ({ row }) => (
          <TextCell value={row.original.ventesDescriptionNumeroVente} />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Auction> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="auction"
          />
        ),
      },
      {
        accessorKey: "ventesDescriptionTitre",
        header: t("ventesDescriptionTitre"),
        size: 500,
        cell: ({ getValue }) => <TextCell value={String(getValue() ?? "")} />,
      },
      {
        accessorKey: "ventesDescriptionDate",
        header: t("ventesDescriptionDate"),
        size: 100,
        cell: ({ row }) => (
          <TextCell value={row.original.ventesDescriptionDate} />
        ),
      },
      {
        accessorKey: "ventesDescriptionVille",
        header: t("ventesDescriptionVille"),
        cell: ({ row }) => (
          <TextCell value={row.original.ventesDescriptionVille} />
        ),
      },
      {
        accessorKey: "ventesDescriptionPays",
        header: t("ventesDescriptionPays"),
        cell: ({ row }) => (
          <TextCell value={row.original.ventesDescriptionPays} />
        ),
      },
      createActionsColumnDef<Auction>(({ row }) => {
          const auctionRow = row.original as Auction;
          if (!multiplesample) return null;
          return (
            <LinkDrawerRowActions
              previewType="auction"
              previewId={auctionRow.id}
              previewElements={[
                {
                  label: t("ventesDescriptionNumeroVente"),
                  value: auctionRow.ventesDescriptionNumeroVente,
                  type: "text",
                },
                {
                  label: t("ventesDescriptionResume"),
                  value: auctionRow.ventesDescriptionResume ?? "",
                  type: "richtext",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInAuctionDrawer
                  fields={fields}
                  key={auctionRow.id}
                  queryId={multiplesample.id}
                  type="multiplesample"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={emptyMultiplesampleInAuction(multiplesample, auctionRow)}
                />
              )}
            />
          );
        }),
    ],
    [multiplesample, fields, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsAuctions} data={notAlreadyRelatedAuctions?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notAlreadyRelatedAuctions?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAdd, onColumnOrderChange: setColumnsOrderAdd, state: { columnVisibility: columnVisibilityAdd, columnOrder: columnsOrderAdd } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinked, onColumnOrderChange: setColumnsOrderLinked, state: { columnVisibility: columnVisibilityLinked, columnOrder: columnsOrderLinked } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <AuctionLinkGridCard item={row.auction} t={t} />
                </GridCardWithActions>
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const AuctionToMultiplesampleLinks = ({
  id,
  type,
  relatedData,
  fields,
  auction,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: AuctionToMultiplesampleLinksProps) => {
  const { t, i18n } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_MS = "auction-links-multiplesample-add";
  const STORAGE_KEY_LINKED_MS = "auction-links-multiplesample-linked";
  const defaultVisibilityMS = {
    ...exemplairesColumnsVisibility,
    author: true,
    actions: true,
  };
  const [columnVisibilityAddMS, setColumnVisibilityAddMS] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_MS);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityMS, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityMS };
    });
  const [columnVisibilityLinkedMS, setColumnVisibilityLinkedMS] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_MS);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityMS, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityMS };
    });
  const [columnsOrderAddMS, setColumnsOrderAddMS] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_MS);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedMS, setColumnsOrderLinkedMS] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_MS);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelatedMultiplesamples } =
    useGetNotRelatedResource<Multiplesample>({
      resultType: "multiplesample",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
      anchorResourceType: type,
    });

  const columnsMultiplesamples: ColumnDef<Multiplesample, unknown>[] = useMemo(
    () => [
      {
        accessorKey:
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        header: t(
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        ),
        cell: ({ row }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ??
              ""
            }
          />
        ),
      },
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionNumero",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
        cell: ({ row }) => (
          <TextCell
            value={
              row.original.exemplaireDansEstampesMultiplesDescriptionNumero ??
              ""
            }
          />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multiplesample"
          />
        ),
      },
      {
        accessorKey:
          "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
        cell: ({ row }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire ??
              ""
            }
          />
        ),
      },
      {
        accessorKey: "author",
        header: t("tab_relatedAuthor"),
        cell: ({ row }) => (
          <TextCell
            value={formatMultiplesampleAuthorDisplay(
              row.original,
              i18n.language as SupportedLocale,
            )}
          />
        ),
      },
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionLocalisation",
        header: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
        cell: ({ row }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionLocalisation ?? ""
            }
          />
        ),
      },
      createActionsColumnDef<Multiplesample>(({ row }) => {
          const multiplesampleRow = row.original as Multiplesample;
          if (!auction) return null;
          return (
            <LinkDrawerRowActions
              previewType="multiplesample"
              previewId={multiplesampleRow.id}
              previewElements={[
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement"),
                  value:
                    multiplesampleRow.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement ??
                    "",
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
                  value:
                    multiplesampleRow.exemplaireDansEstampesMultiplesDescriptionNumero ??
                    "",
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                  value:
                    multiplesampleRow.exemplaireDansEstampesMultiplesDescriptionNotes ??
                    "",
                  type: "richtext",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
                  value:
                    multiplesampleRow.exemplaireDansEstampesMultiplesDescriptionNumeroInventaire ??
                    "",
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
                  value:
                    multiplesampleRow.exemplaireDansEstampesMultiplesDescriptionLocalisation ??
                    "",
                  type: "text",
                },
                {
                  label: t("attachments"),
                  value: multiplesampleRow.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInAuctionDrawer
                  fields={fields}
                  key={multiplesampleRow.id}
                  queryId={auction.id}
                  type="auction"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={emptyMultiplesampleInAuction(multiplesampleRow, auction)}
                />
              )}
            />
          );
        }),
    ],
    [auction, fields, i18n.language, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable columns={columnsMultiplesamples} data={notAlreadyRelatedMultiplesamples?.data ?? []} pagination={{ pagination: getPagination, setPage: setPagination, totalSize: notAlreadyRelatedMultiplesamples?.totalSize ?? 0 }} storageKey={STORAGE_KEY_ADD_MS} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityAddMS, onColumnOrderChange: setColumnsOrderAddMS, state: { columnVisibility: columnVisibilityAddMS, columnOrder: columnsOrderAddMS } }} />
  );
  const listTable = (
    <DataTable columns={columnsLink} data={paginatedRelatedData} pagination={relatedPaginationProps} storageKey={STORAGE_KEY_LINKED_MS} options={{ enableColumnResizing: true, onColumnVisibilityChange: setColumnVisibilityLinkedMS, onColumnOrderChange: setColumnsOrderLinkedMS, state: { columnVisibility: columnVisibilityLinkedMS, columnOrder: columnsOrderLinkedMS } }} />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_MS}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <MultiplesampleLinkGridCard
                  item={row.multiplesample}
                  t={t}
                  onTextClick={() => {
                    setSelectedRelationId(row.id);
                  }}
                />
                </GridCardWithActions>
                                <MultiplesampleInAuctionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="auction"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default MultiplesampleInAuctionLinks;
